import { useQuery } from '@tanstack/react-query'
import { generatePath, useNavigate } from 'react-router'
import { ContentType, IContentReference, ReferenceType } from 'types'
import { WorkspacePath, getContentReferences } from 'utils'
import Collapsible from './Collapsible'
import Flex from './Flex'
import Menu from './Menu'
import Text from './Text'
import { useWorkspace } from './providers'

export interface Props {
  id?: string
  type: ContentType
  /** (default collapsible) */
  variant?: 'collapsible' | 'menu'
}

export default function ContentReferences({ id, variant = 'collapsible', type }: Props) {
  const { workspace } = useWorkspace()
  const navigate = useNavigate()

  const { data: references } = useQuery<IContentReference[]>({
    queryKey: ['contentReferences', id, type, workspace?.id],
    queryFn: async () => {
      const result = await getContentReferences({
        contentId: id!,
        contentType: type,
        workspaceId: workspace!.id,
      })
      return result.data
    },
    enabled: !!workspace?.id && !!id,
  })

  const filtered =
    type === 'content' ? references?.filter(r => r.referencedByType !== 'content') : references

  const getNavigationPath = (referenceType: ReferenceType, referenceId: string) => {
    const workspaceId = workspace!.id
    if (referenceType === 'traits2') return
    return generatePath(`${WorkspacePath}/${referenceType}/:id`, { workspaceId, id: referenceId })
  }

  const referencesByType = filtered?.reduce(
    (acc, reference) => {
      if (!acc[reference.referencedByType]) acc[reference.referencedByType] = []

      acc[reference.referencedByType].push(reference)
      return acc
    },
    {} as Record<ReferenceType, IContentReference[]>
  )

  const count = filtered?.length
  const label = `${count ?? '-'} reference${count === 1 ? '' : 's'}`
  const content = (
    <Flex direction="column" gap={3} p={4}>
      {Object.entries(referencesByType ?? {}).map(([referenceType, typeReferences]) => (
        <Flex key={referenceType} direction="column" gap={2}>
          <Text fontSize="sm" color="gray.500" textTransform="capitalize" fontWeight="medium">
            {referenceType} ({typeReferences.length})
          </Text>
          <Flex direction="column" gap={1} pl={2}>
            {typeReferences.map(
              ({ referencedByType, referencedById, referencedByTitle }: IContentReference) => {
                const path = getNavigationPath(referencedByType, referencedById)
                return (
                  <Text
                    key={referencedById}
                    color={path && 'blue.500'}
                    cursor="pointer"
                    textDecoration="underline"
                    onClick={path != null ? () => navigate(path) : undefined}
                    _hover={path != null ? { color: 'blue.600' } : undefined}
                    fontSize="sm"
                  >
                    {referencedByTitle}
                  </Text>
                )
              }
            )}
          </Flex>
        </Flex>
      ))}
    </Flex>
  )

  if (variant === 'menu') {
    return (
      <Menu disabled={!count} buttonLabel={label} options={[{ type: 'custom', label: content }]} />
    )
  }

  return (
    <Collapsible disabled={!count} label={label}>
      {content}
    </Collapsible>
  )
}
